import { Button, Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import NewObjectForm from './NewObjectForm';
import spinnerTitle from 'components/others/spinnerTitle';
import { useEffect, useState } from 'react';
import SvgSave from 'components/svg/SvgSave';
import * as apiLead from 'api/apiLeads';
import * as apiObject from 'api/apiObjects';
import { useToast } from 'context/ToastContext';
import vconst from 'utils/helpers/structs/vconst';
import { useNavigate } from 'react-router-dom';
import { useSettings } from 'context/SettingsContext';
import type { LeadProposalGenerationType } from '@econtractor/types';
import type { NewObjectType } from '../types';
import { useNeedPay } from 'modules/admin';
import { syncNewObjectPriceFromProposal } from '../helpers/proposalAmountToObjectPrice';

export interface NewObjectModalProps {
    newObject: NewObjectType | null;
    setNewObject: React.Dispatch<React.SetStateAction<NewObjectType | null>>;
    acceptedGeneration?: LeadProposalGenerationType | null;
    acceptedLoading?: boolean;
    onRefreshAccepted?: () => void | Promise<void>;
}

const NewObjectModal = ({
    newObject,
    setNewObject,
    acceptedGeneration = null,
    acceptedLoading = false,
    onRefreshAccepted,
}: NewObjectModalProps) => {
    const { t } = useTranslation(['Objects', 'common']);
    const { showToast } = useToast();
    const { getSettings, localSettings } = useSettings();
    const [creating, setCreating] = useState(false);
    const navigate = useNavigate();
    const { needPay } = useNeedPay();
    const rate = Number(localSettings?.RATE?.current ?? 1) || 1;
    const show = newObject !== null;
    const hasActiveProposal = Boolean(acceptedGeneration);
    const leadId = Number(newObject?.leadId ?? 0);

    useEffect(() => {
        if (!leadId || !acceptedGeneration) return;
        setNewObject((prev) => {
            if (!prev?.leadId) return prev ?? null;
            return syncNewObjectPriceFromProposal(prev, acceptedGeneration, rate);
        });
    }, [acceptedGeneration, leadId, rate, setNewObject]);

    useEffect(() => {
        if (!show) return;
        setCreating(false);
        void onRefreshAccepted?.();
    }, [show, onRefreshAccepted]);

    const handleClickCreate = async () => {
        if (!newObject) return;
        if (needPay()) return;
        setCreating(true);

        if (leadId > 0) {
            const eventData = {
                eventInfo: newObject?.info || '',
                statusID: vconst.leadAccept,
            };
            const resEvent = await apiLead.addEvent(leadId, eventData);
            if (!resEvent) {
                showToast(t('common:messError'), 'danger');
                return setCreating(false);
            }
        }

        const resObject = await apiObject.createObject(newObject as any);

        await getSettings();
        setCreating(false);

        if (resObject) {
            showToast(t('messObjectCreated'), 'success');
            navigate(`/objects/${resObject}`);
        } else showToast(t('common:messError'), 'danger');
    };

    const handleClose = () => {
        setCreating(false);
        setNewObject(null);
    };

    return (
        <Offcanvas
            show={show}
            onHide={handleClose}
            placement="end"
            className="off-new-object"
        >
            <Offcanvas.Header closeButton className="border-bottom">
                <Offcanvas.Title>{t('title')}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                <div className="off-new-object-form d-flex flex-column h-100">
                    <div className="off-new-object-content px-3">
                        {newObject ? (
                            <NewObjectForm
                                newObject={newObject}
                                setNewObject={setNewObject}
                                hasActiveProposal={hasActiveProposal}
                                acceptedLoading={acceptedLoading}
                            />
                        ) : null}
                    </div>

                    <div className="p-3 offcanvas-footer off-new-object-footer">
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="secondary" onClick={handleClose}>
                                {t('common:buttons.close')}
                            </Button>

                            <Button
                                variant="info"
                                type="submit"
                                disabled={
                                    !(newObject?.objectName || '').trim() || creating
                                }
                                onClick={handleClickCreate}
                            >
                                {spinnerTitle(
                                    <>
                                        <SvgSave className="me-2 mtop-2" />
                                        {t('common:buttons.save')}
                                    </>,
                                    creating,
                                    t('common:buttons.saving'),
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default NewObjectModal;
